import React, { useState, useMemo } from 'react';
import { ReactSortable } from 'react-sortablejs';
import classNames from 'classnames';
import IconGripVertical from '@zvuk-b2b/react-uikit/ui/icons/GripVertical';
import { MediafilePreview } from 'application/components/MediafilePreview';
import { useMediafilePreview } from './useMediafilePreview';
import { DATA_TEST_IDS } from './consts';
import { IDraggableVideoMediafile, IVideoStreamEmenuMediafile, IVideoStreamItem } from '../types';
import { assignOrderByIndex, sortItemsByOrder } from '../utils';
import { DraggableListItem } from './DraggableListItem';

import styles from './index.module.scss';
import PreviewItem from './PreviewItem';

export interface DraggableItemListType {
  itemList?: IDraggableVideoMediafile[];
  emenuMediafiles?: IVideoStreamEmenuMediafile[];
  updateItems?: (items: Array<IDraggableVideoMediafile>) => void;
  updateEmenuItems?: (items: IVideoStreamEmenuMediafile[]) => void;
  onSetDuration: (id: string, duration: number) => void;
  onDraftDurationChange?: (id: string, duration: number) => void;
  onRemoveMediafile: (id: string) => void;
  onCopyMediafile: (id: string) => void;
  isBlockedDurationEdit?: boolean;
}

const DraggableVideoMediafiesList: React.FC<DraggableItemListType> = ({
  itemList,
  emenuMediafiles,
  updateItems,
  updateEmenuItems,
  onSetDuration,
  onDraftDurationChange,
  onRemoveMediafile,
  onCopyMediafile,
  isBlockedDurationEdit,
}) => {
  const [localEmenu, setLocalEmenu] = useState<IVideoStreamEmenuMediafile[]>(() => emenuMediafiles ?? []);

  const mergedList = useMemo<IVideoStreamItem[]>(() => {
    const videoItems: IVideoStreamItem[] = (itemList ?? []).map((it) => ({
      ...it,
      itemType: 'video' as const,
    }));
    const emenuItems: IVideoStreamItem[] = localEmenu.map((it) => ({
      ...it,
      itemType: 'emenu' as const,
    }));
    return sortItemsByOrder([...videoItems, ...emenuItems]);
  }, [itemList, localEmenu]);
  const isDragHandleVisible = mergedList.length > 1;
  const { previewResource, requestPreview, closePreview } = useMediafilePreview(mergedList);

  const handleSetList = (newList: IVideoStreamItem[]) => {
    const reordered = assignOrderByIndex(newList);

    const emenuItems = reordered
      .filter((it) => it.itemType === 'emenu')
      .map(({ itemType: _t, ...rest }) => rest as IVideoStreamEmenuMediafile);
    setLocalEmenu(emenuItems);
    updateEmenuItems?.(emenuItems);

    if (!updateItems) return;
    const videoItems = reordered
      .filter((it) => it.itemType === 'video')
      .map(({ itemType: _t, ...rest }) => rest as IDraggableVideoMediafile);
    updateItems(videoItems);
  };

  return (
    <>
      <div data-testid={DATA_TEST_IDS.LIST}>
        <ReactSortable<IVideoStreamItem>
          list={mergedList}
          handle=".draggable"
          disabled={!isDragHandleVisible}
          setList={handleSetList}>
          {mergedList.map((item) => (
            <DraggableListItem key={item.id}>
              <PreviewItem
                className={styles.content}
                leading={
                  isDragHandleVisible ? (
                    <span
                      className={classNames(styles.handle, 'draggable')}
                      data-testid={DATA_TEST_IDS.HANDLE(item.itemType, item.id)}>
                      <IconGripVertical />
                    </span>
                  ) : undefined
                }
                onSetDuration={onSetDuration}
                onDraftDurationChange={onDraftDurationChange}
                file={item}
                onRemoveMediafile={onRemoveMediafile}
                onCopyMediafile={onCopyMediafile}
                onPreview={requestPreview}
                isBlockedDurationEdit={isBlockedDurationEdit}
              />
            </DraggableListItem>
          ))}
        </ReactSortable>
      </div>
      {previewResource && (
        <MediafilePreview
          url={previewResource.uri}
          isVideo={previewResource.kind === 'video'}
          onClose={closePreview}
        />
      )}
    </>
  );
};

export default DraggableVideoMediafiesList;
